import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useDebounce } from "@/lib/useDebounce";
import { Search, UserPlus, Users2, Phone, Mail, X as XIcon, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * GuardianPicker — search + select an existing guardian OR fill in a new one.
 *
 * Props:
 *   value: { mode: "existing" | "new", id?, full_name, relationship, phone, phone_alt, email }
 *   onChange(value)
 *   compact: smaller variant (default false)
 *   onMatchFound(guardian) — optional hook fired when typing reveals an exact phone/email match
 */
export const GuardianPicker = ({ value, onChange, compact = false }) => {
  const [search, setSearch] = useState("");
  const dq = useDebounce(search, 250);
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [matchOpen, setMatchOpen] = useState(false);
  const [match, setMatch] = useState(null);
  const wrapRef = useRef(null);

  // dropdown outside-click close
  useEffect(() => {
    const onDoc = (e) => { if (!wrapRef.current?.contains(e.target)) setShowDropdown(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // live search
  useEffect(() => {
    const term = dq.trim();
    if (!term) { setSuggestions([]); return; }
    api.get("/guardians", { params: { q: term } })
      .then(({ data }) => setSuggestions(data.slice(0, 7)))
      .catch(() => setSuggestions([]));
  }, [dq]);

  const pickExisting = (g) => {
    onChange({
      mode: "existing", id: g.id,
      full_name: g.full_name, relationship: g.relationship,
      phone: g.phone, phone_alt: g.phone_alt, email: g.email,
    });
    setShowDropdown(false); setSearch("");
  };

  const clearSelection = () => {
    onChange({ mode: "new", id: null, full_name: "", relationship: "", phone: "", phone_alt: "", email: "" });
    setSearch("");
  };

  // Run a match check on phone/email blur (only in 'new' mode)
  const checkMatch = async () => {
    if (value.mode !== "new") return;
    if (!value.phone && !value.email) return;
    try {
      const { data } = await api.post("/guardians/match", { phone: value.phone, email: value.email });
      if (data.match) { setMatch(data.guardian); setMatchOpen(true); }
    } catch (e) { /* silent */ }
  };

  const linkToMatch = () => {
    if (match) pickExisting(match);
    setMatchOpen(false); setMatch(null);
  };

  // ----- Mode: existing (selected) -----
  if (value.mode === "existing" && value.id) {
    return (
      <div className={"rounded-lg border border-[#9DCD3F]/40 bg-[#9DCD3F]/8 p-3 " + (compact ? "" : "")} data-testid="guardian-selected">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#1B2C5A] text-[#9DCD3F] grid place-items-center font-display shrink-0">
            {(value.full_name?.[0] || "G").toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-[#1B2C5A] truncate inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#9DCD3F]" /> {value.full_name}
              {value.relationship && <span className="text-xs text-[#475569]">· {value.relationship}</span>}
            </div>
            <div className="text-xs text-[#475569] mt-0.5 flex gap-3 flex-wrap">
              {value.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {value.phone}</span>}
              {value.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {value.email}</span>}
            </div>
            <p className="text-[11px] text-[#9DCD3F] mt-1 italic">Existing guardian — student will be linked to this family.</p>
          </div>
          <Button size="sm" variant="ghost" onClick={clearSelection} className="text-[#475569] hover:text-red-700" data-testid="guardian-clear">
            <XIcon className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    );
  }

  // ----- Mode: new (form) -----
  return (
    <div className="space-y-3" data-testid="guardian-picker">
      <div ref={wrapRef} className="relative">
        <Label className="text-xs text-[#475569] flex items-center gap-1.5 mb-1.5">
          <Search className="w-3 h-3" /> Search existing guardian by name or phone
        </Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
          <Input value={search} onChange={(e) => { setSearch(e.target.value); setShowDropdown(true); }} onFocus={() => setShowDropdown(true)}
                 placeholder="Type a name, phone, or email to find an existing parent…" className="pl-9 h-9 bg-white" data-testid="guardian-search" />
        </div>
        {showDropdown && suggestions.length > 0 && (
          <div className="absolute z-30 left-0 right-0 mt-1 rounded-md border border-[#E2E8F0] bg-white shadow-lg max-h-64 overflow-y-auto" data-testid="guardian-suggestions">
            {suggestions.map(g => (
              <button key={g.id} onClick={() => pickExisting(g)} className="w-full text-left px-3 py-2 hover:bg-[#9DCD3F]/10 border-b border-[#E2E8F0] last:border-b-0" data-testid={`guardian-suggest-${g.id}`}>
                <div className="text-sm text-[#1B2C5A] flex items-center gap-2">
                  <span className="font-medium">{g.full_name}</span>
                  {g.student_count > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#9DCD3F]/20 text-[#1B2C5A] inline-flex items-center gap-1">
                      <Users2 className="w-2.5 h-2.5" /> {g.student_count} child{g.student_count !== 1 ? "ren" : ""}
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#475569] mt-0.5 flex gap-2">
                  {g.phone && <span>{g.phone}</span>}
                  {g.email && <span>· {g.email}</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="relative flex items-center gap-2 text-xs text-[#475569]">
        <div className="flex-1 h-px bg-[#E2E8F0]" />
        <span>OR enter new guardian</span>
        <div className="flex-1 h-px bg-[#E2E8F0]" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Fld label="Full Name"><Input value={value.full_name || ""} onChange={(e) => onChange({ ...value, mode: "new", full_name: e.target.value })} data-testid="guardian-name" /></Fld>
        <Fld label="Relationship"><Input value={value.relationship || ""} onChange={(e) => onChange({ ...value, mode: "new", relationship: e.target.value })} placeholder="Father / Mother / Uncle / …" data-testid="guardian-relationship" /></Fld>
        <Fld label="Phone (Primary)"><Input value={value.phone || ""} onChange={(e) => onChange({ ...value, mode: "new", phone: e.target.value })} onBlur={checkMatch} data-testid="guardian-phone" /></Fld>
        <Fld label="Phone (Alt)"><Input value={value.phone_alt || ""} onChange={(e) => onChange({ ...value, mode: "new", phone_alt: e.target.value })} /></Fld>
        <div className="col-span-2"><Fld label="Email"><Input type="email" value={value.email || ""} onChange={(e) => onChange({ ...value, mode: "new", email: e.target.value })} onBlur={checkMatch} data-testid="guardian-email" /></Fld></div>
      </div>

      {/* Match confirmation modal */}
      <Dialog open={matchOpen} onOpenChange={setMatchOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-[#1B2C5A] flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-500" /> Existing Guardian Found
            </DialogTitle>
          </DialogHeader>
          {match && (
            <div className="space-y-3">
              <p className="text-sm text-[#475569]">A guardian with this phone/email already exists. Link this student to them?</p>
              <div className="rounded-lg border border-[#E2E8F0] p-3 bg-[#FDFBF7]">
                <div className="text-sm font-medium text-[#1B2C5A]">{match.full_name}</div>
                <div className="text-xs text-[#475569] mt-0.5 flex gap-2 flex-wrap">
                  {match.phone && <span>{match.phone}</span>}
                  {match.email && <span>· {match.email}</span>}
                </div>
                {match.students?.length > 0 && (
                  <div className="mt-2">
                    <div className="text-xs text-[#475569] mb-1">Already linked children:</div>
                    <div className="flex gap-2 flex-wrap">
                      {match.students.map(s => (
                        <span key={s.id} className="text-xs px-2 py-0.5 rounded-full bg-[#1B2C5A] text-white inline-flex items-center gap-1">
                          {s.photo && <img src={s.photo} alt={s.full_name} className="w-4 h-4 rounded-full object-cover" />}
                          {s.full_name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMatchOpen(false)} data-testid="match-keep-new">Keep as new</Button>
            <Button onClick={linkToMatch} className="bg-[#9DCD3F] hover:bg-[#88BB35] text-[#1B2C5A]" data-testid="match-link-existing">
              <Users2 className="w-4 h-4 mr-1.5" /> Link to existing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Fld = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-[#1B2C5A] text-xs">{label}</Label>{children}</div>
);

export default GuardianPicker;
